import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import { useState } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  Phone,
  MapPin,
  Calendar,
  ExternalLink,
  Download,
  Loader2,
  Copy,
  CheckCircle,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

function formatDate(d) {
  if (!d) return '-';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}

function downloadCSV(jobs, dateRange) {
  const headers = ['WO#', 'Customer', 'Phone', 'Asset', 'Serial Number', 'Scheduled Date', 'Location'];
  const rows = [headers.join(',')];
  for (const j of jobs) {
    rows.push([
      j.work_order_number || '',
      `"${(j.customer_name || '').replace(/"/g, '""')}"`,
      j.customer_phone || '',
      `"${(j.asset_name || '').replace(/"/g, '""')}"`,
      j.asset_serial_number || '',
      j.scheduled_date || '',
      j.workshop_location || '',
    ].join(','));
  }
  const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `unbooked-jobs-${dateRange || 'export'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function UnbookedJobs() {
  const { selectedLocationId, currentLocation } = useLocationContext();
  const queryClient = useQueryClient();
  const [weekOffset, setWeekOffset] = useState(-1);
  const [toast, setToast] = useState(null);
  const [pushingId, setPushingId] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['unbooked-jobs', weekOffset, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/admin/unbooked-jobs', { params: { weekOffset } });
      return data;
    },
  });

  const pushMutation = useMutation({
    mutationFn: async (jobUid) => {
      setPushingId(jobUid);
      const { data } = await axios.post('/api/admin/push-job', { jobUid });
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['unbooked-jobs'] });
      showToast(`Pushed to ${formatDate(result.newDate)}`);
    },
    onError: (err) => showToast(err?.response?.data?.error || 'Failed to push job', 'error'),
    onSettled: () => setPushingId(null),
  });

  const jobs = data?.jobs || [];
  const dateRange = data?.dateRange || '';
  const zuperAppUrl = 'https://web.zuperpro.com';

  const getWeekLabel = () => {
    if (weekOffset === -1) return 'Last Week';
    if (weekOffset === 0) return 'Current Week';
    if (weekOffset > 0) return `${weekOffset} Weeks Ahead`;
    return `${Math.abs(weekOffset)} Weeks Ago`;
  };

  return (
    <Layout>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-brand-primary text-white'
        }`}>
          {toast.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-amber-100 to-amber-50 rounded-xl flex items-center justify-center shadow-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Unbooked Jobs</h1>
            <p className="text-xs text-brand-neutral hidden md:block">TBC jobs from past weeks — customer didn't book</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => jobs.length > 0 && downloadCSV(jobs, dateRange)}
            disabled={jobs.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-secondary transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        {/* Week Navigation + Stats */}
        <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeekOffset(o => o - 1)}
                className="p-2 rounded-xl hover:bg-brand-light/60 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-brand-dark" />
              </button>
              <div>
                <p className="text-lg font-bold text-brand-dark">{getWeekLabel()}</p>
                <p className="text-sm text-brand-neutral">{dateRange}</p>
              </div>
              <button
                onClick={() => setWeekOffset(o => Math.min(o + 1, -1))}
                disabled={weekOffset >= -1}
                className="p-2 rounded-xl hover:bg-brand-light/60 transition-colors disabled:opacity-30"
              >
                <ChevronRight className="w-5 h-5 text-brand-dark" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="border-2 border-amber-300 rounded-xl px-4 py-2 text-center min-w-[90px]">
                <p className="text-xl font-bold text-amber-600">{isLoading ? '-' : jobs.length}</p>
                <p className="text-[10px] font-semibold text-brand-neutral uppercase tracking-wider">TBC Jobs</p>
              </div>
              <div className="border border-brand-light rounded-xl px-4 py-2 text-center min-w-[90px]">
                <p className="text-xl font-bold text-brand-dark">{isLoading ? '-' : new Set(jobs.map(j => j.customer_uid)).size}</p>
                <p className="text-[10px] font-semibold text-brand-neutral uppercase tracking-wider">Customers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-brand-light/80 shadow-sm overflow-hidden">
          {isLoading || isFetching ? (
            <div className="p-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-primary" />
              <p className="text-sm text-brand-neutral mt-2">Loading...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-lg font-bold text-brand-dark">All Clear!</p>
              <p className="text-sm text-brand-neutral">No unbooked TBC jobs for this week.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gradient-to-r from-brand-light/50 to-brand-light/20 text-brand-dark font-semibold border-b border-brand-light/60">
                  <tr>
                    <th className="px-4 py-3">Job #</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Asset</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-light/50">
                  {jobs.map(job => (
                    <tr key={job.job_uid} className="hover:bg-brand-light/20 transition-colors group">
                      <td className="px-4 py-3 font-mono text-xs text-brand-neutral">
                        <div className="inline-flex items-center gap-1 group/wo">
                          {job.work_order_number || 'N/A'}
                          <button
                            onClick={() => window.open(`${zuperAppUrl}/jobs/${job.job_uid}/details`, '_blank')}
                            className="p-0.5 rounded hover:bg-brand-light hover:text-brand-primary transition-all cursor-pointer opacity-0 group-hover/wo:opacity-60"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                          {job.work_order_number && (
                            <button
                              onClick={() => { navigator.clipboard.writeText(String(job.work_order_number)); showToast('Copied!'); }}
                              className="p-0.5 rounded hover:bg-brand-light hover:text-brand-primary transition-all cursor-pointer opacity-0 group-hover/wo:opacity-60"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-brand-dark">{job.customer_name}</div>
                        {job.customer_phone && (
                          <div className="flex items-center gap-1 text-[11px] text-brand-neutral">
                            <Phone className="w-3 h-3" />
                            {job.customer_phone}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-brand-neutral">
                        {job.asset_name ? (
                          <div>
                            <div>{job.asset_name}</div>
                            {job.asset_serial_number && <div className="font-mono text-[11px] text-brand-neutral/70">{job.asset_serial_number}</div>}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-brand-neutral" />
                          {formatDate(job.scheduled_date)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-brand-neutral">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {job.workshop_location || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => pushMutation.mutate(job.job_uid)}
                          disabled={pushingId === job.job_uid}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-primary bg-brand-light rounded-lg hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-50"
                          title="Push to next week (+7 days)"
                        >
                          {pushingId === job.job_uid ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                          Push +7 Days
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </Layout>
  );
}
